import { Injectable } from '@angular/core';
import { SessionContextService } from '../tracker/session-context.service';
import { ApiService } from '../api/api.service';
import { environment } from '../../../environments/environment';
import { BrowserStorage } from '../../models/browserStorage';
import { PdfSession } from '../../models/pdfSession';

@Injectable({
	providedIn: 'root',
})
export class PdfTrackerService {
	private currentSession: PdfSession | null = null;

	constructor(
		private sessionContext: SessionContextService,
		private api: ApiService,
	) {}

	startSession(pdfId: string): void {
		if (!pdfId) {
			return;
		}

		if (this.currentSession) {
			this.endSession();
		}

		this.currentSession = {
			sessionId: crypto.randomUUID(),
			courseSessionId: this.sessionContext.courseSessionId!,
			chapterSessionId: this.sessionContext.chapterSessionId!,
			categoryId: this.sessionContext.categoryId!,
			courseId: this.sessionContext.courseId!,
			chapterId: this.sessionContext.chapterId!,
			pdfId,
			startedAt: Date.now(),
		};
	}

	async endSession(): Promise<void> {
		const payload = this.buildPayload();
		if (!payload) {
			return;
		}

		try {
			await this.api.post('/tracker/pdf/flush', payload);
		} catch (err) {
			console.error('Failed to flush pdf activity', err);
		}
	}

	flushViaBeacon(): void {
		const payload = this.buildPayload();
		if (!payload) {
			return;
		}

		fetch(`${environment.API_URL}/tracker/pdf/flush`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': BrowserStorage.get('userToken')
			},
			body: JSON.stringify(payload),
			keepalive: true
		});
	}

	private buildPayload() {
		if (!this.currentSession) {
			return null;
		}

		if (!this.currentSession.courseSessionId || !this.currentSession.chapterSessionId) {
			this.currentSession = null;
			return null;
		}

		const endedAt = Date.now();
		const payload = {
			sessionId: this.currentSession.sessionId,
			courseSessionId: this.currentSession.courseSessionId,
			chapterSessionId: this.currentSession.chapterSessionId,
			categoryId: this.currentSession.categoryId,
			courseId: this.currentSession.courseId,
			chapterId: this.currentSession.chapterId,
			pdfId: this.currentSession.pdfId,
			startedAt: this.currentSession.startedAt,
			endedAt,
			viewedSeconds: Math.max(0, Math.round((endedAt - this.currentSession.startedAt) / 1000)),
		};

		this.currentSession = null;
		return payload;
	}
}
